import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { Clock, Eye, Folders, CalendarDays, Timer } from "lucide-react";
import DatePicker from "react-datepicker";
import Header from "../Header";
import LeftNavBar from "../LeftNavBar";
import PageHeader from "../PageHeader";
import { PageHeaderName } from "../../StyledCss/styledComponents";
import LoadingView from "../LoadingView";
import FailureView from "../FailureView";
import { StatCard } from "./StatCard";

import { getProjectsSummary } from "../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./index.css";
import { PrimaryButton } from "../../StyledCss/styledbtn";

const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const WorkLogsAdminDashBoard = () => {
  const navigate = useNavigate();
  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);
  const [projectsData, setProjectsData] = useState([]);
  const [startMonth, setStartMonth] = useState(new Date());
  const [endMonth, setEndMonth] = useState(new Date());
  const [yearlyProjectsData, setYearlyProjectsData] = useState({});
    const [projectStatus, setProjectStatus] = useState({
      active: true,
      closed: false,
      paused: false,
    });

  const fetchProjectsSummary = useCallback(async () => {
    try {
      setApiStatus(apiStatusConstants.inProgress);
      
      const startDate = new Date(
        startMonth.getFullYear(),
        startMonth.getMonth(),
        1,
      );
      const endDate = new Date(
        endMonth.getFullYear(),
        endMonth.getMonth() + 1,
        0,
      );

      const formatToYMD = (date) =>
        date.getFullYear() +
        "-" +
        String(date.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(date.getDate()).padStart(2, "0");
      const formattedStartDate = formatToYMD(startDate);
      const formattedEndDate = formatToYMD(endDate);

      const selectedStatuses = Object.keys(projectStatus).filter(key => projectStatus[key]);
      const statusParam = selectedStatuses.length > 0 ? selectedStatuses.join(',') : null;

      const response = await getProjectsSummary(
        formattedStartDate,
        formattedEndDate,
        statusParam
      );

      if (response.ok) {
        const data = convertKeysToCamelCase(await response.json());
        setProjectsData(data.projects);
        setYearlyProjectsData(data.allProjects);
        setApiStatus(apiStatusConstants.success);
      } else {
        setApiStatus(apiStatusConstants.failure);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setApiStatus(apiStatusConstants.failure);
    }
  }, [startMonth, endMonth, projectStatus]);

 useEffect(() => {
  fetchProjectsSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [startMonth, endMonth, projectStatus]);

  // Memoized stats data to prevent recalculation
  const statsData = useMemo(() => {
    return [
      {
        title: "Total Projects",
        value: yearlyProjectsData.totalProjects || 0,
        icon: Folders,
        accentColor: "#2563EB",
      },
      {
        title: "Total Working Days",
        value: yearlyProjectsData.totalWorkingDays || 0,
        icon: CalendarDays,
        accentColor: "#059669",
      },
      {
        title: "Total Billable Hours",
        value: yearlyProjectsData.actualWorkingHours || 0,
        icon: Clock,
        accentColor: "#4F46E5",
      },
      {
        title: "Logged Billable Hours",
        value: yearlyProjectsData.loggedHours || 0,
        icon: Timer,
        accentColor: "#4F46E5",
      },
    ];
  }, [yearlyProjectsData]);

  const handleViewUsers = useCallback(
    (data) => {
      navigate(`/worklogs_project/${data.projectId}`);
    },
    [navigate],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "PROJECT NAME",
        field: "projectName",
        flex: 1,
        minWidth: 200,
        suppressMenu: true
      },
      {
        headerName: "STATUS",
        field: "status",
        minWidth: 200,
        suppressMenu: true
      },

      {
        headerName: "WORKING HOURS",
        field: "loggedHours",
        minWidth: 150,
        suppressMenu: true
      },
      {
        headerName: "ACTIONS",
        width: 120,
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              height: "100%",
            }}
          >
            <div title="View" className="icon-wrapper">
              <Eye
                style={{
                  cursor: "pointer",
                  color: "#28a745",
                  fontSize: "20px",
                }}
                title="View"
                onClick={() => handleViewUsers(params.data)}
              />
            </div>
          </div>
        ),
        sortable: false,
        filter: false,
        floatingFilter: false,
      },
    ],
    [handleViewUsers],
  );

  const defaultColDef = useMemo(
    () => ({
      cellStyle: { fontSize: "14px" },
      sortable: true,
      filter: true,
      floatingFilter: true,
      resizable: true,
    }),
    [],
  );

  const renderTable = useCallback(
    () => (
      <div
        className="ag-theme-alpine"
        style={{
          height: "78vh",
          fontSize: "14px",
          margin: "20px",
          fontFamily: "Arial, Helvetica, sans-serif",
          backgroundColor: "#FFFFFF",
        }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          rowData={projectsData}
          pagination={true}
          paginationPageSize={10}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          animateRows={true}
          enableCellTextSelection={true}
        />
      </div>
    ),
    [columnDefs, projectsData, defaultColDef],
  );
    const handleNavigate = useCallback(
      () => {
        // Own-logs dashboard. /worklogs routes view_all holders (everyone who
        // sees this button) to this admin dashboard by default, so ask the
        // entry point for the personal view explicitly.
        navigate(`/worklogs`, { state: { personalWorklogs: true } });
      },
      [navigate],
    );

  const renderData = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
        return <LoadingView />;
      case apiStatusConstants.success:
        return renderTable();
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={fetchProjectsSummary} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <Header />
      <div className="d-flex">
        <LeftNavBar />
        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName>Worklogs</PageHeaderName>
            <div
              style={{ display: "flex", gap: "15px", alignItems: "center" }}
            >
              <PrimaryButton onClick={handleNavigate}>Add Worklog</PrimaryButton>
            </div>
          </PageHeader>

          {apiStatus !== apiStatusConstants.failure && (
            <>
              <div className="row g-3 mx-0">
                {statsData.map((item, index) => (
                  <div
                    key={index}
                    className="col-6 col-md-3 col-lg-3 col-xl-2 d-flex"
                  >
                    <StatCard {...item} />
                  </div>
                ))}
              </div>

              <div className="d-flex justify-content-between">
                <h4 className="fw-semibold m-3">Projects Overview</h4>
                <div className="d-flex flex-row flex-wrap justify-content-end align-items-center mb-2">
                  <div className="month-picker d-flex flex-column m-1">
                    <label className="mb-1 ">Start Month</label>
                    <DatePicker
                      className="date-input-label"
                      selected={startMonth}
                      onChange={(date) => {
                        if (date) {
                          setStartMonth(date);
                          if (date > endMonth) {
                            setEndMonth(date);
                          }
                        } else {
                          setStartMonth(new Date());
                        }
                      }}
                      showMonthYearPicker
                      dateFormat="MMMM yyyy"
                    />
                  </div>
                  <div className="month-picker d-flex flex-column m-1">
                    <label className="mb-1 ">End Month</label>
                    <DatePicker
                      className="date-input-label"
                      selected={endMonth}
                      onChange={(date) => {
                        if (date) {
                          setEndMonth(date);
                        } else {
                          setEndMonth(new Date());
                        }
                      }}
                      showMonthYearPicker
                      dateFormat="MMMM yyyy"
                      minDate={startMonth}
                    />
                  </div>
                </div>
              </div>
               <div className="d-flex justify-content-end gap-3 mx-3 mb-3">
            <label className="d-flex align-items-center gap-1">
              <input
                type="checkbox"
                checked={projectStatus.active}
                onChange={(e) =>
                  setProjectStatus({ ...projectStatus, active: e.target.checked })
                }
              />
              Active
            </label>
            <label className="d-flex align-items-center gap-1">
              <input
                type="checkbox"
                checked={projectStatus.paused}
                onChange={(e) =>
                  setProjectStatus({ ...projectStatus, paused: e.target.checked })
                }
              />
              Paused
            </label>
            <label className="d-flex align-items-center gap-1">
              <input
                type="checkbox"
                checked={projectStatus.closed}
                onChange={(e) =>
                  setProjectStatus({ ...projectStatus, closed: e.target.checked })
                }
              />
              Closed
            </label>
            
          </div>
            </>
          )}

          <div className="table-container">{renderData()}</div>
        </div>
      </div>
    </div>
  );
};

export default WorkLogsAdminDashBoard;
